import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../../hooks/useAuth'
import { getSuppliers, updateSupplier, deleteSupplier } from '../../../services/supplierService'
import { showAlert, showConfirm } from '../../../services/dialogService'
import InfoButton from '../../../components/InfoButton/InfoButton'
import Loader from '../../../components/Loader/Loader'
import EditVendor from './EditVendor'
import '../Section.css'
import './NumbersPlease.css'
import './TheChosenOnes.css'

function TheChosenOnes() {
  const { user, loading: authLoading } = useAuth()
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingSupplier, setEditingSupplier] = useState(null)

  useEffect(() => {
    if (!user) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await getSuppliers(user.id, true)
        setSuppliers(data)
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [user])

  const handleUnbook = async (supplier) => {
    const ok = await showConfirm('Unbook this supplier? They will move back to Numbers, please.', { title: 'Unbook', confirmLabel: 'Unbook', cancelLabel: 'Cancel' })
    if (!ok) return
    try {
      await updateSupplier(supplier.id, { booked: false })
      setSuppliers(prev => prev.filter(s => s.id !== supplier.id))
    } catch (err) {
      console.error(err)
      await showAlert('Error updating supplier')
    }
  }

  const formatCurrency = (v) => {
    const n = parseFloat(v || 0)
    return isNaN(n) ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  }

  const formatDate = (d) => {
    if (!d) return '—'
    try {
      const date = new Date(d)
      return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    } catch {
      return '—'
    }
  }

  if (authLoading || loading) return <Loader />

  return (
    <div className="dashboard-section supplier-chosen-ones">
      <h1>
        SUPPLIER MANAGER — THE CHOSEN ONES
        <InfoButton
          title="The chosen ones"
          content={
            <div>
              <p>
                Here&apos;s your Dream Team — the legends who actually made the cut. Keep track of their arrival and wrap-up times, 
                crew meals, and payments (because even magic needs a schedule).
              </p>
              <p><strong>Features:</strong></p>
              <ul>
                <li>Manage contracted suppliers with full details</li>
                <li>Track arrival and end times, meal requirements, dietaries</li>
                <li>Record initial and second payments with due dates</li>
                <li>Click a supplier to edit details, contacts, payments</li>
              </ul>
            </div>
          }
        />
      </h1>
      <div className="supplier-chosen-table-section">
          <div className="supplier-table-header">
            <h2>Vendors</h2>
            <div className="supplier-table-actions">
              <button onClick={() => showAlert('Export coming soon')}>Export</button>
              <button onClick={() => window.print()}>Print</button>
            </div>
          </div>

          <div className="supplier-table-wrap">
            <table className="supplier-table supplier-chosen-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Business Name</th>
                  <th>Contact Name</th>
                  <th>Phone / Email</th>
                  <th>Web</th>
                  <th>Arrival Time</th>
                  <th>End Time</th>
                  <th>Meal Required</th>
                  <th>Dietaries</th>
                  <th>Inclusions/Notes</th>
                  <th>Total Cost Package</th>
                  <th>Initial Payment</th>
                  <th>Due Date</th>
                  <th>Paid</th>
                  <th>2nd Payment</th>
                  <th>Due Date</th>
                  <th>Paid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s) => (
                  <tr 
                    key={s.id} 
                    className={editingSupplier?.id === s.id ? 'selected' : ''}
                    onClick={() => setEditingSupplier(s)}
                  >
                    <td>{s.category || '—'}</td>
                    <td>{s.name || s.business_name || '—'}</td>
                    <td>{s.contact_name || '—'}</td>
                    <td>{[s.phone, s.email].filter(Boolean).join(' / ') || '—'}</td>
                    <td>{s.web ? <a href={s.web} target="_blank" rel="noopener noreferrer">Web</a> : '—'}</td>
                    <td>{s.arrival_time || '—'}</td>
                    <td>{s.end_time || '—'}</td>
                    <td>{s.meal_required ? 'Yes' : 'No'}</td>
                    <td>{s.dietaries || '—'}</td>
                    <td>{s.inclusions_notes || s.notes || '—'}</td>
                    <td>{formatCurrency(s.total_cost_package || s.rate)}</td>
                    <td>{formatCurrency(s.initial_payment)}</td>
                    <td>{formatDate(s.initial_due_date)}</td>
                    <td>{s.initial_paid ? '✓' : '—'}</td>
                    <td>{formatCurrency(s.second_payment)}</td>
                    <td>{formatDate(s.second_due_date)}</td>
                    <td>{s.second_paid ? '✓' : '—'}</td>
                    <td className="supplier-td-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="supplier-btn-edit" onClick={() => setEditingSupplier(s)}>Edit</button>
                      <button className="supplier-btn-unbook" onClick={() => handleUnbook(s)}>Unbook</button>
                    </td>
                  </tr>
                ))}
                {suppliers.length === 0 && (
                  <tr>
                    <td colSpan="17" className="supplier-empty">
                      No booked suppliers yet. Go to &quot;Numbers, please&quot; and mark suppliers as Booked.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      {editingSupplier && (
        <div
          className="supplier-edit-modal"
          onClick={async (e) => {
            if (e.target !== e.currentTarget) return
            const ok = await showConfirm('Leave without saving? Your changes to this vendor will not be saved.', { title: 'Discard changes?', confirmLabel: 'Leave', cancelLabel: 'Stay', variant: 'danger' })
            if (ok) setEditingSupplier(null)
          }}
        >
          <div className="supplier-edit-modal-content" onClick={(e) => e.stopPropagation()}>
            <EditVendor
              supplier={editingSupplier}
              onClose={() => setEditingSupplier(null)}
              onSave={(updated) => {
                setSuppliers(prev => prev.map(s => s.id === updated.id ? updated : s))
                setEditingSupplier(null)
              }}
              onDelete={() => {
                setSuppliers(prev => prev.filter(s => s.id !== editingSupplier.id))
                setEditingSupplier(null)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default TheChosenOnes
